import { Button } from "@/components/ui/button";
import { Bell, Brain, Image as ImageIcon, ListChecks } from "lucide-react";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";

// ChatBuddy: our own AI assistant in WhatsApp, running on DeepSeek models.
const WHATSAPP_URL = "https://wa.me/31644532278?text=Hi%20ChatBuddy!";

const FEATURES = [
  { icon: Brain, text: "Remembers what you tell it, across every chat" },
  { icon: Bell, text: "Sets reminders that arrive in WhatsApp" },
  { icon: ListChecks, text: "Keeps your shopping and to-do lists" },
  { icon: ImageIcon, text: "Understands voice notes, photos and PDFs" },
];

const ChatBuddyPromo = () => (
  <section id="chatbuddy" aria-labelledby="chatbuddy-title" className="container mx-auto px-4 pb-16">
    <div className="max-w-5xl mx-auto rounded-2xl border border-[#25D366]/30 bg-gradient-to-br from-[#25D366]/10 via-white to-white shadow-md overflow-hidden">
      <div className="grid md:grid-cols-[1fr_auto] gap-8 p-6 md:p-10 items-center">
        <div className="min-w-0">
          <p className="inline-flex items-center gap-2 rounded-full bg-[#25D366]/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#128C7E] mb-4">
            <WhatsAppIcon className="h-3.5 w-3.5" />
            New: ChatBuddy for WhatsApp
          </p>
          <h2 id="chatbuddy-title" className="text-3xl md:text-4xl font-bold text-foreground mb-3 text-balance">
            Chat with DeepSeek in WhatsApp
          </h2>
          <p className="text-muted-foreground text-lg mb-6 max-w-2xl">
            ChatBuddy is a personal AI assistant that lives in your WhatsApp and runs on DeepSeek V4.1 Flash, with
            DeepSeek V4 Pro for harder questions. Just send a message, no app or account needed.
          </p>
          <ul className="grid sm:grid-cols-2 gap-3 mb-8">
            {FEATURES.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-start gap-3 text-sm text-foreground">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#25D366]/15 text-[#128C7E]">
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <span>{text}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-4">
            <Button asChild size="lg" className="bg-[#25D366] hover:bg-[#1ebe5b] text-white">
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <WhatsAppIcon className="mr-2 h-5 w-5" />
                Start chatting for free
              </a>
            </Button>
            <p className="text-sm text-muted-foreground">
              Free to start. Plus from €4.99 a month for the smartest model and voice replies.
            </p>
          </div>
          <p className="mt-6 text-xs text-muted-foreground">
            Built by us, not by DeepSeek. For people aged 16 and over in the EU, EEA and Turkey.{" "}
            <a href="/chatbuddy/terms" className="underline hover:text-foreground">Terms</a>
            {" · "}
            <a href="/chatbuddy/privacy-policy" className="underline hover:text-foreground">Privacy</a>
          </p>
        </div>

        <div className="hidden md:flex flex-col items-center gap-3">
          <div className="rounded-xl bg-white p-3 shadow-sm ring-1 ring-slate-200">
            <img
              src="/chatbuddy-whatsapp-qr.svg"
              alt="QR code that opens a WhatsApp chat with ChatBuddy"
              width={160}
              height={160}
              loading="lazy"
            />
          </div>
          <p className="text-sm text-muted-foreground text-center max-w-[11rem]">
            Scan with your phone to open ChatBuddy in WhatsApp
          </p>
        </div>
      </div>
    </div>
  </section>
);

export default ChatBuddyPromo;
